import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FaSyncAlt, FaChartLine, FaDownload, FaPrint } from "react-icons/fa";
import { API_BASE_URL } from "../config/constants";
import { formatStoredMoney } from "../utils/currency";
import { downloadCSV, downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportControlBar from "../components/ReportControlBar";
import ReportPeriodPicker from "../components/ReportPeriodPicker";

const emptyReport = {
  summary: {
    total_sales: 0,
    total_orders_count: 0,
    avg_order_value: 0,
    total_other_income: 0,
    total_revenue: 0,
    total_expense: 0,
    net_profit: 0,
    profit_margin_percent: 0,
    expense_ratio_percent: 0,
    is_profitable: true,
  },
  by_category: { expense: [], income: [] },
  daily_trend: [],
};

const ymd = (date) => {
  const d = new Date(date);
  const pad = (value) => String(value).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthStart = () => ymd(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
const money = (value) => formatStoredMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const categoryName = (row) => row?.category_name || row?.name || "Uncategorized";
const categoryAmount = (row) => Number(row?.total_amount ?? row?.amount ?? 0);

const ProfitLossReportPage = () => {
  const [restaurantId, setRestaurantId] = useState(null);
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(() => ymd(new Date()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [categoryTab, setCategoryTab] = useState("expense");
  const [report, setReport] = useState(emptyReport);
  const reportRef = useRef(null);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const user = stored?.data || stored || {};
    const id = user?.id || user?.restaurant_id;
    if (!id) {
      toast.error("Restaurant information is missing. Please login again.");
      setLoading(false);
      return;
    }
    setRestaurantId(id);
  }, []);

  const fetchReport = useCallback(async ({ refresh = false } = {}) => {
    if (!restaurantId) return;
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/finance/profit-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: restaurantId, from_date: fromDate, to_date: toDate }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || "Unable to load Profit & Loss report");
      }
      setReport({
        summary: { ...emptyReport.summary, ...(json?.summary || {}) },
        by_category: {
          expense: Array.isArray(json?.by_category?.expense) ? json.by_category.expense : [],
          income: Array.isArray(json?.by_category?.income) ? json.by_category.income : [],
        },
        daily_trend: Array.isArray(json?.daily_trend) ? json.daily_trend : [],
      });
    } catch (error) {
      toast.error(error?.message || "Unable to load Profit & Loss report");
      setReport(emptyReport);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [restaurantId, fromDate, toDate]);

  useEffect(() => {
    if (restaurantId) fetchReport();
  }, [restaurantId, fetchReport]);

  const summary = report.summary || emptyReport.summary;
  const revenue = Number(summary.total_revenue || 0);
  const salesShare = revenue > 0 ? Math.round((Number(summary.total_sales || 0) / revenue) * 100) : 0;
  const incomeShare = revenue > 0 ? Math.round((Number(summary.total_other_income || 0) / revenue) * 100) : 0;
  const expenseShare = revenue > 0 ? Math.round((Number(summary.total_expense || 0) / revenue) * 100) : 0;
  const activeCategories = report.by_category?.[categoryTab] || [];
  const exportRows = useMemo(() => (report.daily_trend || []).map((row) => [
    row.date,
    Number(row.sales || 0),
    Number(row.income ?? row.other_income ?? 0),
    Number(row.expense || 0),
    Number(row.net_profit || 0),
    Number(row.orders_count || 0),
  ]), [report.daily_trend]);

  return (
    <Page>
      <ReportPageHeader
        title="Profit & Loss Report"
        subtitle="Sales + other income − expenses, with daily and category breakdown."
        stats={[
          { label: Number(summary.net_profit || 0) >= 0 ? "Net Profit" : "Net Loss", value: money(summary.net_profit) },
          { label: "Total Revenue", value: money(summary.total_revenue) },
          { label: "Total Expense", value: money(summary.total_expense) },
          { label: "Profit Margin", value: `${Number(summary.profit_margin_percent || 0).toFixed(2)}%` },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker fromDate={fromDate} toDate={toDate} onChange={({ from, to }) => { setFromDate(from); setToDate(to); }} />
        <Refresh type="button" onClick={() => fetchReport({ refresh: true })} disabled={refreshing}>
          <FaSyncAlt /> {refreshing ? "Refreshing…" : "Refresh"}
        </Refresh>
      </ReportControlBar>

      {loading ? <StateCard>Loading Profit &amp; Loss report…</StateCard> : (
        <Report ref={reportRef}>
          <NetCard $positive={summary.is_profitable !== false && Number(summary.net_profit || 0) >= 0}>
            <div><span>{Number(summary.net_profit || 0) >= 0 ? "Net Profit" : "Net Loss"}</span><strong>{money(summary.net_profit)}</strong></div>
            <Margin><FaChartLine /> {Number(summary.profit_margin_percent || 0).toFixed(2)}% margin</Margin>
          </NetCard>

          <KpiGrid>
            <Kpi><span>Total Sales</span><strong>{money(summary.total_sales)}</strong><small>{summary.total_orders_count || 0} orders · {money(summary.avg_order_value)} average</small></Kpi>
            <Kpi><span>Other Income</span><strong>{money(summary.total_other_income)}</strong><small>Income recorded in Finance Tracker</small></Kpi>
            <Kpi><span>Total Revenue</span><strong>{money(summary.total_revenue)}</strong><small>Sales + other income</small></Kpi>
            <Kpi $danger><span>Total Expense</span><strong>{money(summary.total_expense)}</strong><small>{Number(summary.expense_ratio_percent || 0).toFixed(2)}% of revenue</small></Kpi>
          </KpiGrid>

          <Card>
            <CardTitle>Revenue &amp; Expense Comparison</CardTitle>
            <Bars>
              <BarRow><span>Sales</span><Bar><i style={{ width: `${Math.min(100, salesShare)}%`, background: "#2563eb" }} /></Bar><b>{salesShare}%</b></BarRow>
              <BarRow><span>Other income</span><Bar><i style={{ width: `${Math.min(100, incomeShare)}%`, background: "#059669" }} /></Bar><b>{incomeShare}%</b></BarRow>
              <BarRow><span>Expense</span><Bar><i style={{ width: `${Math.min(100, expenseShare)}%`, background: "#dc2626" }} /></Bar><b>{expenseShare}%</b></BarRow>
            </Bars>
          </Card>

          <Card>
            <CardTitle>Daily Breakdown</CardTitle>
            <TableWrap><Table><thead><tr><th>Date</th><th>Orders</th><th>Sales</th><th>Income</th><th>Expense</th><th>Net</th></tr></thead><tbody>
              {(report.daily_trend || []).map((row) => <tr key={row.date}><td>{row.date}</td><td>{row.orders_count || 0}</td><td>{money(row.sales)}</td><td>{money(row.income ?? row.other_income)}</td><td>{money(row.expense)}</td><td className={Number(row.net_profit || 0) >= 0 ? "positive" : "negative"}>{money(row.net_profit)}</td></tr>)}
              {!report.daily_trend?.length && <tr><td colSpan="6" className="empty">No data for this date range.</td></tr>}
            </tbody></Table></TableWrap>
          </Card>

          <Card>
            <CardHead><CardTitle>Category Breakdown</CardTitle><Tabs><Chip $active={categoryTab === "expense"} onClick={() => setCategoryTab("expense")}>Expenses</Chip><Chip $active={categoryTab === "income"} onClick={() => setCategoryTab("income")}>Other Income</Chip></Tabs></CardHead>
            <TableWrap><Table><thead><tr><th>#</th><th>Category</th><th>Entries</th><th>Amount</th></tr></thead><tbody>
              {activeCategories.map((row, index) => <tr key={`${categoryName(row)}-${index}`}><td>{index + 1}</td><td>{categoryName(row)}</td><td>{row.entries_count ?? row.count ?? "—"}</td><td>{money(categoryAmount(row))}</td></tr>)}
              {!activeCategories.length && <tr><td colSpan="4" className="empty">No {categoryTab} categories for this range.</td></tr>}
            </tbody></Table></TableWrap>
          </Card>
        </Report>
      )}

      <ExportActions>
        <button onClick={() => downloadPDF(reportRef.current, `profit_loss_${fromDate}_to_${toDate}.pdf`)}><FaDownload /> PDF</button>
        <button onClick={() => printThermal(reportRef.current, `profit_loss_${fromDate}_to_${toDate}`)}><FaPrint /> Thermal Print</button>
        <button onClick={() => downloadExcel(`profit_loss_${fromDate}_to_${toDate}.xlsx`, ["Date", "Sales", "Other Income", "Expense", "Net Profit", "Orders"], exportRows)}>Excel</button>
        <button onClick={() => downloadCSV(`profit_loss_${fromDate}_to_${toDate}.csv`, ["Date", "Sales", "Other Income", "Expense", "Net Profit", "Orders"], exportRows)}>CSV</button>
      </ExportActions>
    </Page>
  );
};

export default ProfitLossReportPage;

const Page = styled.div`min-height:100vh;padding:18px;background:#f3f6fa;color:#0f172a;@media(max-width:650px){padding:12px;}`;
const Refresh = styled.button`border:0;background:linear-gradient(135deg,#075f9e,#0786c5);color:#fff;border-radius:10px;padding:9px 14px;font-weight:800;display:flex;gap:7px;align-items:center;cursor:pointer;box-shadow:0 4px 12px rgba(7,95,158,.15);&:disabled{opacity:.65;cursor:not-allowed}`;
const Chip = styled.button`border:1px solid ${({$active})=>$active?"#059669":"#cbd5e1"};background:${({$active})=>$active?"#ecfdf5":"#fff"};color:${({$active})=>$active?"#047857":"#475569"};padding:8px 11px;border-radius:999px;font-weight:800;cursor:pointer`;
const StateCard = styled.div`padding:50px;text-align:center;background:#fff;border-radius:18px;color:#64748b`;
const Report = styled.div`display:grid;gap:14px`;
const NetCard = styled.div`display:flex;justify-content:space-between;align-items:center;gap:15px;flex-wrap:wrap;padding:22px;border-radius:18px;background:${({$positive})=>$positive?"#ecfdf5":"#fef2f2"};border:1px solid ${({$positive})=>$positive?"#a7f3d0":"#fecaca"};div{display:grid;gap:5px}span{color:#64748b;font-weight:800}strong{font-size:2rem;color:${({$positive})=>$positive?"#047857":"#b91c1c"}}`;
const Margin = styled.b`display:flex;gap:7px;align-items:center;padding:9px 12px;background:#fff;border-radius:999px`;
const KpiGrid = styled.div`display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;@media(max-width:950px){grid-template-columns:repeat(2,minmax(0,1fr))}@media(max-width:520px){grid-template-columns:1fr}`;
const Kpi = styled.div`padding:16px;background:#fff;border:1px solid ${({$danger})=>$danger?"#fecaca":"#e2e8f0"};border-radius:16px;display:grid;gap:6px;span{font-size:12px;color:#64748b;font-weight:800}strong{font-size:1.25rem;color:${({$danger})=>$danger?"#b91c1c":"#0f172a"}}small{color:#94a3b8}`;
const Card = styled.div`padding:17px;background:#fff;border:1px solid #e2e8f0;border-radius:17px;box-shadow:0 5px 18px rgba(15,23,42,.04)`;
const CardHead = styled.div`display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap`;
const CardTitle = styled.h3`margin:0 0 13px;font-size:1rem`;
const Tabs = styled.div`display:flex;gap:6px;margin-bottom:10px`;
const Bars = styled.div`display:grid;gap:12px`;
const BarRow = styled.div`display:grid;grid-template-columns:110px 1fr 45px;align-items:center;gap:10px;font-size:13px;b{text-align:right}`;
const Bar = styled.div`height:10px;border-radius:999px;background:#e2e8f0;overflow:hidden;i{display:block;height:100%;border-radius:inherit}`;
const TableWrap = styled.div`overflow-x:auto;border:1px solid #e2e8f0;border-radius:12px`;
const Table = styled.table`width:100%;border-collapse:collapse;min-width:650px;th,td{padding:11px 12px;text-align:left;border-bottom:1px solid #eef2f7;font-size:13px}th{background:#f8fafc;color:#475569;font-size:11px;text-transform:uppercase}.positive{color:#047857;font-weight:900}.negative{color:#dc2626;font-weight:900}.empty{text-align:center;color:#94a3b8;padding:28px}`;
const ExportActions = styled.div`display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap;margin-top:15px;button{border:0;border-radius:10px;padding:10px 13px;background:#015b9a;color:#fff;font-weight:800;display:flex;align-items:center;gap:6px;cursor:pointer}`;
